import React, { ComponentProps, useCallback, useEffect } from 'react';

import { useSnackbar } from 'notistack';
import { TourDetailsFormResources, useTourDetailsFormQuery } from '~/queries/tours/useTourDetailsFormQueries';

import { Alert, AlertTitle, Button, CircularProgress } from '@mui/material';

import ExtendedRJSForm from '../RJSF/ExtendedRJSForm';

import validateTourDetailsFormData from './validateTourCampaignSchedule';

interface Props {
  tourId: string;
}

export default function TourCampaignScheduleDetailsForm(props: Props) {
  const { tourId } = props;
  const { enqueueSnackbar } = useSnackbar();

  const { fetchRequestInstance, fetch, patchRequestInstance, patch } = useTourDetailsFormQuery(
    TourDetailsFormResources.CAMPAIGN_SCHEDULE,
  );

  useEffect(() => {
    fetch(tourId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId]);

  const handleSubmission = useCallback<ComponentProps<typeof ExtendedRJSForm>['onSubmit']>(
    ({ formData }) => {
      patch(tourId, formData);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tourId],
  );

  useEffect(() => {
    switch (patchRequestInstance.status) {
      case 'succeeded':
        enqueueSnackbar('Campaign schedule updated successfully.', {
          variant: 'success',
        });
        break;
      case 'failed':
        enqueueSnackbar(`Submission failed! ${patchRequestInstance.error}`, {
          variant: 'error',
        });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchRequestInstance]);

  if (fetchRequestInstance.status === 'pending' || fetchRequestInstance.status === 'uninitiated') {
    return <CircularProgress />;
  }

  if (fetchRequestInstance.status === 'failed') {
    return (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            variant="outlined"
            onClick={() => {
              fetch(tourId);
            }}
          >
            Retry
          </Button>
        }
      >
        <AlertTitle>Failed to fetch the form.</AlertTitle>
        {fetchRequestInstance.error}
      </Alert>
    );
  }

  const { result } = fetchRequestInstance;
  return (
    <>
      <ExtendedRJSForm
        id="tour-campaign-schedule-details-form"
        schema={result.schema}
        uiSchema={result.uiSchema}
        formData={result.formData}
        onSubmit={handleSubmission}
        busy={patchRequestInstance.status === 'pending'}
        customValidate={validateTourDetailsFormData}
      />
      {patchRequestInstance.status === 'failed' && <Alert severity="error">{patchRequestInstance.error}</Alert>}
    </>
  );
}
